import type { Question } from '@/types';
import { defaultRng, randomInt, shuffle, weightedPick, type Rng } from '@/utils/random';

/**
 * QuestionEngine — gera contas de tabuada e adapta a dificuldade ao
 * desempenho da criança.
 *
 * Decisões de design:
 *
 * 1. **Nível define o universo de tabuadas e o tempo por conta** (CLAUDE.md
 *    §4.3). Quanto mais alto o nível, mais tabuadas entram no pool e menor
 *    o tempo limite.
 *
 * 2. **Spaced repetition leve**: tabuadas em que a criança erra mais entram
 *    com peso maior na próxima geração. Domínio (>= MASTERY_THRESHOLD em
 *    janela de WINDOW_SIZE tentativas) reduz a frequência. Nunca chega a
 *    zero — a criança ainda revê o que já sabe, só com menos frequência.
 *
 * 3. **Distratores plausíveis**: erros típicos de tabuada são vizinhos do
 *    resultado (off-by-one nos fatores, soma trocada por produto). Geramos
 *    candidatos próximos e filtramos. Nunca usar distratores absurdos como
 *    `7×8 = 12`.
 *
 * 4. **RNG injetável** para testes determinísticos.
 *
 * 5. **Sistema puro** (sem dependência de Phaser). Pode rodar no Node nos
 *    testes Vitest e ser reutilizado em qualquer cena.
 */

const WINDOW_SIZE = 10;
const MASTERY_THRESHOLD = 0.9;
const BASE_WEIGHT = 1;
const ERROR_WEIGHT_BOOST = 4; // erros recentes pesam mais que acertos

interface LevelTier {
  /** Nível (inclusive) em que este tier vale. */
  maxLevel: number;
  /** Tabuadas que entram no pool até este nível. */
  tables: readonly number[];
  /** Tempo por conta em ms. */
  timeLimitMs: number;
  /** Dificuldade visível (1..5) — usada por UI/analytics. */
  difficulty: 1 | 2 | 3 | 4 | 5;
}

const LEVEL_TIERS: readonly LevelTier[] = [
  { maxLevel: 3, tables: [1, 2, 5, 10], timeLimitMs: 8000, difficulty: 1 },
  { maxLevel: 6, tables: [1, 2, 3, 4, 5, 10], timeLimitMs: 6000, difficulty: 2 },
  { maxLevel: 9, tables: [1, 2, 3, 4, 5, 6, 7, 10], timeLimitMs: 5000, difficulty: 3 },
  { maxLevel: 12, tables: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10], timeLimitMs: 4000, difficulty: 4 },
  // Mestre Geek: 11 e 12 entram, todas as tabuadas misturadas.
  { maxLevel: Infinity, tables: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], timeLimitMs: 3000, difficulty: 5 },
];

interface TableStats {
  /** Resultados das últimas WINDOW_SIZE tentativas (true = acerto). */
  recent: boolean[];
}

export interface QuestionEngineOptions {
  /** Nível 1..N. Default: 1. */
  level?: number;
  /** RNG injetável (testes). */
  rng?: Rng;
  /**
   * Override do pool de tabuadas. Quando definido, ignora o tier do
   * nível e usa exatamente essas tabuadas (útil para fases específicas
   * com curva própria — ver data/worlds.ts).
   */
  tables?: readonly number[];
  /** Override do tempo por conta (ms). Mesma lógica do `tables`. */
  timeLimitMs?: number;
}

export class QuestionEngine {
  private level: number;
  private readonly rng: Rng;
  private readonly stats = new Map<number, TableStats>();
  private readonly tablesOverride: readonly number[] | null;
  private readonly timeLimitOverride: number | null;

  constructor(options: QuestionEngineOptions = {}) {
    this.level = Math.max(1, options.level ?? 1);
    this.rng = options.rng ?? defaultRng;
    this.tablesOverride = options.tables && options.tables.length > 0 ? [...options.tables] : null;
    this.timeLimitOverride = typeof options.timeLimitMs === 'number' ? options.timeLimitMs : null;
  }

  setLevel(level: number): void {
    this.level = Math.max(1, level);
  }

  getLevel(): number {
    return this.level;
  }

  getDifficulty(): 1 | 2 | 3 | 4 | 5 {
    return this.currentTier().difficulty;
  }

  getTimeLimitMs(): number {
    return this.timeLimitOverride ?? this.currentTier().timeLimitMs;
  }

  /** Tabuadas ativas (cópia, segura para uso externo). */
  getActiveTables(): number[] {
    return this.tablesOverride ? [...this.tablesOverride] : [...this.currentTier().tables];
  }

  /** Gera a próxima Question com seleção ponderada por desempenho. */
  generate(): Question {
    const tables = this.tablesOverride ?? this.currentTier().tables;
    const weights = tables.map((t) => this.weightForTable(t));

    const factorA = weightedPick(tables, weights, this.rng);
    // O segundo fator vai de 1 a 12 — caso clássico da tabuada.
    const factorB = randomInt(1, 12, this.rng);
    const correctAnswer = factorA * factorB;

    const distractors = this.generateDistractors(factorA, factorB, correctAnswer);
    const options = shuffle([correctAnswer, ...distractors], this.rng);

    return {
      factorA,
      factorB,
      correctAnswer,
      options,
      difficulty: this.getDifficulty(),
    };
  }

  /**
   * Registra o resultado de uma tentativa para alimentar a adaptação.
   * Mantemos uma janela deslizante por tabuada (ambos os fatores contam,
   * porque errar 7×8 sinaliza fragilidade tanto no 7 quanto no 8).
   */
  recordResult(question: Question, correct: boolean): void {
    this.pushStat(question.factorA, correct);
    if (question.factorB !== question.factorA) {
      this.pushStat(question.factorB, correct);
    }
  }

  /** Limpa todo o histórico — útil ao trocar de jogador. */
  reset(): void {
    this.stats.clear();
  }

  private currentTier(): LevelTier {
    return LEVEL_TIERS.find((tier) => this.level <= tier.maxLevel) ?? LEVEL_TIERS[LEVEL_TIERS.length - 1]!;
  }

  private pushStat(table: number, correct: boolean): void {
    const stats = this.stats.get(table) ?? { recent: [] };
    stats.recent.push(correct);
    if (stats.recent.length > WINDOW_SIZE) {
      stats.recent.shift();
    }
    this.stats.set(table, stats);
  }

  private weightForTable(table: number): number {
    const stats = this.stats.get(table);
    if (!stats || stats.recent.length === 0) {
      return BASE_WEIGHT;
    }
    const correctCount = stats.recent.filter((r) => r).length;
    const accuracy = correctCount / stats.recent.length;

    // Domínio: amostra cheia + alta precisão → peso reduzido (mas não zero).
    if (stats.recent.length >= WINDOW_SIZE && accuracy >= MASTERY_THRESHOLD) {
      return BASE_WEIGHT * 0.3;
    }
    // Caso geral: peso cresce conforme a taxa de erro recente.
    const errorRate = 1 - accuracy;
    return BASE_WEIGHT + ERROR_WEIGHT_BOOST * errorRate;
  }

  /**
   * Distratores plausíveis: vizinhos do resultado correto.
   * Sempre retorna 3 valores distintos, todos > 0 e diferentes do correto.
   */
  private generateDistractors(factorA: number, factorB: number, correct: number): number[] {
    const candidates = new Set<number>();
    // Off-by-one nos fatores
    candidates.add((factorA - 1) * factorB);
    candidates.add((factorA + 1) * factorB);
    candidates.add(factorA * (factorB - 1));
    candidates.add(factorA * (factorB + 1));
    // Adjacentes ao resultado
    candidates.add(correct + factorA);
    candidates.add(correct - factorA);
    candidates.add(correct + factorB);
    candidates.add(correct - factorB);
    // Confusão soma↔produto (apenas quando soma fica próxima — evita
    // distratores absurdamente pequenos).
    const sum = factorA + factorB;
    if (sum > 0 && Math.abs(sum - correct) <= correct) {
      candidates.add(sum);
    }

    const valid = [...candidates].filter((n) => n > 0 && n !== correct);
    const picked = shuffle(valid, this.rng).slice(0, 3);

    // Fallback: se não houver candidatos suficientes (acontece com 1×1, 2×1
    // etc.), preencher com inteiros próximos não usados.
    let bump = 1;
    while (picked.length < 3) {
      const candidate = correct + bump;
      if (candidate > 0 && candidate !== correct && !picked.includes(candidate)) {
        picked.push(candidate);
      }
      bump = bump > 0 ? -bump : -bump + 1;
    }

    return picked;
  }
}
